import React, { useState } from 'react';
import { ETHNICITIES, LANGUAGES, SKILLS, CORE_SKILLS } from './filterEnums';
import {
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    Box,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Slider,
    FormControl,
    useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CustomButton from '../Button/CustomButton';
import { makeStyles } from '@mui/styles';
import LocationAutocomplete from '../LocationAutocomplete/LocationAutocomplete';
import AutocompleteMultiSelect from '../AutocompleteMultiSelect/AutocompleteMultiSelect';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { useSearchParams } from 'react-router-dom';
import { TProfileType } from '../../types/global/helper';

interface TalentFiltersProps {
    filters: any;
    onChange: (filters: any) => void;
    profileType?: TProfileType;
}

const genderOptions = ['male', 'female', 'others'];
const hairTypeOptions = ['straight', 'wavy', 'curly', 'any'];
const bodyTypeOptions = ['athletic', 'average', 'curvy', 'any'];

const useStyles = makeStyles(theme => ({
    root: {
        borderRadius: 12,
        padding: 24,
        marginBottom: 24,
        minWidth: 320,
        width: '100%',
    },
    filterChip: {
        margin: '0 8px 8px 0',
    },
    filterSection: {
        marginBottom: 16,
    },
    filterRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 24,
        marginBottom: 16,
    },
    filterField: {
        minWidth: 120,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
    },
    buttonGroup: {
        display: 'flex',
        gap: 16,
        justifyContent: 'flex-end',
        marginTop: 16,
    },
    label: {
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontWeight: 600,
        fontSize: 14,
        marginBottom: 4,
    },
}));

const TalentFilters: React.FC<TalentFiltersProps> = ({ filters, onChange, profileType = 'performer' }) => {
    const classes = useStyles();
    const { themeStyle } = useThemeContext();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [local, setLocal] = useState(filters || {});
    const [locationValue, setLocationValue] = useState<any>(null);
    const [, setSearchParams] = useSearchParams();

    // Get skills based on profile type
    const availableSkills = CORE_SKILLS[profileType] || SKILLS;

    // Age slider
    const handleAgeChange = (_: any, newValue: number | number[]) => {
        const [min, max] = newValue as number[];
        setLocal({ ...local, ageFrom: min, ageTo: max });
    };

    // Rating slider
    const handleRatingChange = (_: any, value: number | number[]) => {
        setLocal({ ...local, rating: value });
    };

    // Gender
    const handleGenderChange = (e: any) => {
        setLocal({ ...local, gender: e.target.value });
    };

    // Skills/Languages - now handled directly in AutocompleteMultiSelect components

    // Appearance
    const handleEthnicityChange = (e: any) => {
        setLocal({ ...local, ethnicity: e.target.value });
    };
    const handleHairColorChange = (e: any) => {
        setLocal({ ...local, hair_color: e.target.value });
    };
    const handleHairTypeChange = (e: any) => {
        setLocal({ ...local, hair_type: e.target.value });
    };
    const handleBodyTypeChange = (e: any) => {
        setLocal({ ...local, body_type: e.target.value });
    };

    // Location (LocationAutocomplete)
    const handleLocationChange = (address: string, coordinates?: number[]) => {
        // Here, you would use geocoding to get lat/lng for the selected address
        // For demo, just set dummy lat/lng and store the address
        if (coordinates) {
            setLocationValue({ address });
            setLocal({ ...local, location: address, lat: coordinates[0], lng: coordinates[1] });
        }
    };

    // Apply filters
    const handleApply = () => {
        // Map local state to search query as needed
        onChange(local);
    };

    const handleReset = () => {
        setLocal({});
        setLocationValue(null);
        onChange({});
        setSearchParams({});
    };

    return (
        <Paper
            className={classes.root}
            sx={{
                bgcolor: theme[themeStyle].secondaryBg,
                p: 3,
            }}>
            {/* Basic Filters */}
            <Accordion
                defaultExpanded
                sx={{
                    bgcolor: theme[themeStyle].secondaryBg,
                    border: `1px solid ${theme[themeStyle].grey}`,
                    '&:before': {
                        display: 'none',
                    },
                }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontWeight: 600,
                            fontSize: 14,
                            mb: 1,
                        }}>
                        Basic Filters
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                    <Box className={classes.filterRow} sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Age Range
                            </Typography>
                            <Slider
                                value={[local.ageFrom || 18, local.ageTo || 60]}
                                min={10}
                                max={100}
                                step={1}
                                onChange={handleAgeChange}
                                valueLabelDisplay="auto"
                                sx={{
                                    width: '100%',
                                    color: theme[themeStyle].highlight,
                                    '& .MuiSlider-thumb': { bgcolor: theme[themeStyle].highlight },
                                    '& .MuiSlider-track': { bgcolor: theme[themeStyle].highlight },
                                    '& .MuiSlider-rail': { bgcolor: theme[themeStyle].tag_white, opacity: 0.2 },
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Gender
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={local.gender || ''}
                                    onChange={handleGenderChange}
                                    displayEmpty
                                    className={classes.filterField}
                                    size="small"
                                    sx={{
                                        bgcolor: theme[themeStyle].secondaryBg,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        },
                                    }}>
                                    <MenuItem value="">Any Gender</MenuItem>
                                    {genderOptions.map(g => (
                                        <MenuItem key={g} value={g} style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            {g}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Min Rating
                            </Typography>
                            <Slider
                                value={local.rating || 0}
                                min={0}
                                max={5}
                                step={0.1}
                                onChange={handleRatingChange}
                                valueLabelDisplay="auto"
                                sx={{
                                    width: '100%',
                                    color: theme[themeStyle].highlight,
                                    '& .MuiSlider-thumb': { bgcolor: theme[themeStyle].highlight },
                                    '& .MuiSlider-track': { bgcolor: theme[themeStyle].highlight },
                                    '& .MuiSlider-rail': { bgcolor: theme[themeStyle].tag_white, opacity: 0.2 },
                                }}
                            />
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
            {/* Skills & Languages */}
            <Accordion
                sx={{
                    bgcolor: theme[themeStyle].secondaryBg,
                    border: `1px solid ${theme[themeStyle].grey}`,
                    '&:before': {
                        display: 'none',
                    },
                }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontWeight: 600,
                            fontSize: 14,
                            mb: 1,
                        }}>
                        Skills & Languages
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                    <Box className={classes.filterRow} sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
                        <Box sx={{ flex: 1 }}>
                            <AutocompleteMultiSelect
                                options={[...availableSkills]}
                                value={local.skills || []}
                                onChange={value => setLocal({ ...local, skills: value })}
                                placeholder="Search and select skills..."
                                label="Skills"
                                size="small"
                                className={classes.filterField}
                            />
                        </Box>
                        {/* <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Languages
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    multiple
                                    value={local.languages || []}
                                    onChange={handleLanguagesChange}
                                    className={classes.filterField}
                                    size="small"
                                    sx={{
                                        bgcolor: theme[themeStyle].secondaryBg,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        },
                                    }}
                                    renderValue={selected => (selected as string[]).join(', ')}>
                                    {LANGUAGES.map(l => (
                                        <MenuItem key={l} value={l} style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            {l}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box> */}
                        <Box sx={{ flex: 1 }}>
                            <AutocompleteMultiSelect
                                options={[...LANGUAGES]}
                                value={local.language_spoken || []}
                                onChange={value => setLocal({ ...local, language_spoken: value })}
                                placeholder="Search and select languages..."
                                label="Language Spoken"
                                size="small"
                                className={classes.filterField}
                            />
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
            {/* Appearance & Type */}
            <Accordion
                sx={{
                    bgcolor: theme[themeStyle].secondaryBg,
                    border: `1px solid ${theme[themeStyle].grey}`,
                    '&:before': {
                        display: 'none',
                    },
                }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontWeight: 600,
                            fontSize: 14,
                            mb: 1,
                        }}>
                        Appearance
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                    <Box className={classes.filterRow} sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Ethnicity
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    multiple
                                    value={local.ethnicity || []}
                                    onChange={handleEthnicityChange}
                                    className={classes.filterField}
                                    size="small"
                                    sx={{
                                        bgcolor: theme[themeStyle].secondaryBg,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        },
                                    }}
                                    renderValue={selected => (selected as string[]).join(', ')}>
                                    {ETHNICITIES.map(ei => (
                                        <MenuItem key={ei} value={ei} style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            {ei}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Hair Color
                            </Typography>
                            <FormControl fullWidth>
                                <TextField
                                    value={local.hair_color || ''}
                                    onChange={handleHairColorChange}
                                    className={classes.filterField}
                                    size="small"
                                    placeholder="Hair Color"
                                    InputProps={{
                                        style: {
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            background: theme[themeStyle].secondaryBg,
                                        },
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}
                                />
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Hair Type
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={local.hair_type || ''}
                                    onChange={handleHairTypeChange}
                                    displayEmpty
                                    className={classes.filterField}
                                    size="small"
                                    sx={{
                                        bgcolor: theme[themeStyle].secondaryBg,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        },
                                    }}>
                                    <MenuItem value="">Any Hair Type</MenuItem>
                                    {hairTypeOptions.map(ht => (
                                        <MenuItem key={ht} value={ht} style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            {ht}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Body Type
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={local.body_type || ''}
                                    onChange={handleBodyTypeChange}
                                    displayEmpty
                                    className={classes.filterField}
                                    size="small"
                                    sx={{
                                        bgcolor: theme[themeStyle].secondaryBg,
                                        color: theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        '& .MuiSelect-icon': { color: theme[themeStyle].tag_white },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme[themeStyle].tag_white,
                                        },
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: theme[themeStyle].secondaryBg,
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            },
                                        },
                                    }}>
                                    <MenuItem value="">Any Body Type</MenuItem>
                                    {bodyTypeOptions.map(bt => (
                                        <MenuItem key={bt} value={bt} style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            {bt}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
            {/* Location */}
            <Accordion
                sx={{
                    bgcolor: theme[themeStyle].secondaryBg,
                    border: `1px solid ${theme[themeStyle].grey}`,
                    borderRadius: `0 0 4px 4px`,
                    '&:before': {
                        display: 'none',
                    },
                }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: theme[themeStyle].tag_white }} />}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontWeight: 600,
                            fontSize: 14,
                            mb: 1,
                        }}>
                        Location
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: theme[themeStyle].secondaryBg }}>
                    <Box className={classes.filterRow} sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{ color: theme[themeStyle].tag_white, fontFamily: FONT_ENUM.MAIN_MEDIUM, mb: 1 }}>
                                Search & Select Location
                            </Typography>
                            <LocationAutocomplete
                                value={locationValue}
                                onSelect={handleLocationChange}
                                withCordinates
                                onSelectLocationOption={() => {}}
                                searchOptions={{ types: ['(regions)'] }}
                                placeholder="Search state or country..."
                                fieldStyle={{
                                    bgcolor: theme[themeStyle].secondaryBg,
                                    borderRadius: '8px',
                                    input: { color: theme[themeStyle].tag_white },
                                    marginTop: '0px',
                                    label: { color: theme[themeStyle].tag_white },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>
            {/* Buttons */}
            <Box className={classes.buttonGroup}>
                <CustomButton
                    onClick={handleReset}
                    buttonProps={{ variant: 'outlined' }}
                    label="Reset"
                    customStyle={{ borderRadius: '5px', minWidth: '100px' }}
                    textStyle={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    }}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'none',
                        },
                        borderColor: theme[themeStyle].highlight,
                    }}
                />
                <CustomButton
                    onClick={handleApply}
                    buttonProps={{ variant: 'contained' }}
                    label="Apply"
                    customStyle={{ borderRadius: '5px', minWidth: '100px' }}
                />
            </Box>
        </Paper>
    );
};

export default TalentFilters;
